"use client";

import type { ImageContent, Rating } from "@homeinstead/schemas";
import type { PortableTextBlock } from "next-sanity";
import type { HeroVariant } from "~www/components/LeadForm/CareSeeker";
import type { LeadSource } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { HeroIconVariant } from "~www/types/national-hero";

import { Suspense } from "react";
import {
  CareSeekerNationalLeadForm,
  HeroImage,
} from "~www/components/LeadForm/CareSeeker";
import { DEFAULT_SITE_BUILDER_LEAD_SOURCE } from "~www/components/LeadForm/CareSeeker/lead-source-values";
import { VideoPlayButton } from "~www/components/VideoPlayButton";
import { useMultiStepForm } from "~www/hooks/useMultiStepForm";
import { CaregiverDrawerWithSearchParams } from "./CaregiverDrawerWithSearchParams";

// Note that the displayed step and the actual step is off by 1 because the first step is hidden.
// Also, the final step is a Thank You page.
const INITIAL_STEP = 2;
const FINAL_STEP = 6;

export const NationalHero = ({
  heroHeading,
  placeholders,
  heroImageLarge,
  heroImageSmall,
  iconVariant,
  variant = "light",
  showCTAButton = false,
  showCaregiverCTA = false,
  rating,
  phoneNumber,
  leadSource = DEFAULT_SITE_BUILDER_LEAD_SOURCE,
}: {
  heroHeading: PortableTextBlock[];
  placeholders?: Record<string, string>;
  heroImageLarge?: ImageContent;
  heroImageSmall?: ImageContent;
  iconVariant?: HeroIconVariant;
  variant?: HeroVariant;
  showCTAButton?: boolean;
  showCaregiverCTA?: boolean;
  rating?: Rating;
  phoneNumber: string;
  leadSource?: LeadSource;
}) => {
  const careSeekerShallowForm = useMultiStepForm({
    finalStepIndex: FINAL_STEP,
    initialStepIndex: INITIAL_STEP,
  });

  const handleScrollToVideo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const videoElement = document.getElementById("video-primary");
    if (videoElement) {
      videoElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="relative flex flex-col items-start overflow-hidden xl:min-h-[800px] xl:flex-row xl:pb-20">
      <CareSeekerNationalLeadForm
        className="mx-auto px-4 pt-8 pb-14 sm:px-10 lg:px-[72px] xl:w-full 2xl:px-[152px]"
        currentStep={careSeekerShallowForm.currentStepIndex}
        finalStep={FINAL_STEP}
        handleBack={careSeekerShallowForm.goToPreviousStep}
        handleNext={careSeekerShallowForm.goToNextStep}
        heroHeading={heroHeading}
        iconVariant={iconVariant}
        leadSource={leadSource}
        phoneNumber={phoneNumber}
        placeholders={placeholders}
        rating={rating}
        variant={variant}
      />

      <div className="relative h-full">
        <HeroImage
          heroImageLarge={heroImageLarge}
          heroImageSmall={heroImageSmall}
          showCareGiverCTA={showCaregiverCTA}
          showLargeViewportImage={
            // On large viewports, the hero image should only be displayed on the initial lead form step.
            careSeekerShallowForm.currentStepIndex === INITIAL_STEP
          }
        />

        {showCTAButton &&
          careSeekerShallowForm.currentStepIndex === INITIAL_STEP && (
            <VideoPlayButton onClick={handleScrollToVideo} />
          )}

        <Suspense fallback={null}>
          <CaregiverDrawerWithSearchParams />
        </Suspense>
      </div>
    </div>
  );
};
